import React from 'react';
import { ChevronLeft, Close } from '@mui/icons-material';
import {
  Box,
  Button,
  ButtonOwnProps,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { AsyncButton } from './Button/AsyncButton';

export interface ChoiceDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  actions?: {
    color?: ButtonOwnProps['color'];
    name: string;
    form?: React.ReactNode;
    action?: () => Promise<void>;
    icon?: JSX.Element;
    variant?: ButtonOwnProps['variant'];
    disabled?: boolean;
  }[];
  children: React.ReactNode;
}

export const ChoiceDialog: React.FC<ChoiceDialogProps> = ({
  children,
  actions,
  open,
  onClose,
  title,
}) => {
  const [page, setPage] = React.useState(-1);
  React.useEffect(() => {
    setPage(-1);
  }, [open]);

  const goHome = () => {
    setPage(-1);
  };

  return (
    <Dialog scroll="paper" open={open} onClose={onClose}>
      <DialogTitle width={400}>
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <Box
            sx={{ visibility: page > -1 ? 'visible' : 'hidden', display: 'flex', flexShrink: 1 }}
          >
            <IconButton onClick={goHome}>
              <ChevronLeft />
            </IconButton>
          </Box>
          <Box
            sx={{
              justifyContent: 'center',
              display: 'flex',
              flex: 1,
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                overflow: 'hidden',
                textAlign: 'center',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
            >
              {title}
            </Typography>
            <Typography variant="caption" sx={{ textAlign: 'center' }}>
              {actions?.[page]?.name}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexShrink: 1 }}>
            <IconButton size="small" sx={{ ml: 'auto' }} onClick={onClose}>
              <Close fontSize="small" color="action" />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ height: 400, width: 400 }} dividers>
        {page === -1 ? children : actions?.[page]?.form}
      </DialogContent>

      <Collapse in={!!actions?.length && page === -1}>
        <DialogActions>
          {actions?.map((a, i) =>
            a.action ? (
              <AsyncButton
                fullWidth
                sx={{ ml: 1, mr: 1 }}
                variant="outlined"
                key={a.name}
                enableDefaultPadding
                onClick={a.action}
                color={a.color}
                disabled={a.disabled}
              >
                {a.name}
              </AsyncButton>
            ) : (
              <Button
                color={a.color}
                sx={{ mr: 1, ml: 1 }}
                fullWidth
                size="small"
                variant={a.variant || 'outlined'}
                onClick={() => setPage(i)}
                key={a.name}
                startIcon={a.icon}
                disabled={a.disabled}
                disableElevation
              >
                {a.name}
              </Button>
            ),
          )}
        </DialogActions>
      </Collapse>
    </Dialog>
  );
};
